import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Form, Modal, Popconfirm, Select, Space, Table, message } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axios from '../../api';
import dayjs from 'dayjs';

const { Option } = Select;

const AgentMemberManage = ({ agentId, onBack, showTitle = false }) => {
  const tableWrapRef = useRef(null);
  const [tableScrollY, setTableScrollY] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [agentName, setAgentName] = useState('');
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `共 ${total} 条`,
  });

  const fetchMembers = async (page = 1, pageSize = 10) => {
    if (!agentId) return;
    setLoading(true);
    try {
      const response = await axios.get('/api/agent-user-relations/', {
        params: { agent: agentId, page, page_size: pageSize, ordering: '-created' },
      });
      const memberList = response.data.results || response.data || [];
      setMembers(memberList);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: response.data.count ?? memberList.length,
      }));
    } catch (error) {
      message.error('获取成员列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users/', { params: { page_size: 1000 } });
      const userList = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setUsers(userList);
    } catch (error) {
      message.error('获取用户列表失败');
    }
  };

  useEffect(() => {
    if (!agentId) return;
    axios.get(`/api/agents/${agentId}/`).then((res) => {
      setAgentName(res.data.name || '');
    }).catch(() => setAgentName(''));
    fetchMembers(1, pagination.pageSize);
    fetchUsers();
  }, [agentId]);

  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return undefined;
    const updateScrollY = () => {
      const height = el.clientHeight;
      const reserved = 56;
      setTableScrollY(height > reserved ? height - reserved : undefined);
    };
    updateScrollY();
    const observer = new ResizeObserver(updateScrollY);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const memberUserIds = members.map((m) => (typeof m.user === 'object' ? m.user?.id : m.user));
  const availableUsers = users.filter((u) => !memberUserIds.includes(u.id));

  const handleAddMember = async (values) => {
    try {
      await axios.post('/api/agent-user-relations/', {
        agent_id: Number(agentId),
        user_id: values.user_id,
        start_time: values.start_time ? values.start_time.format('YYYY-MM-DD HH:mm:ss') : null,
        end_time: values.end_time ? values.end_time.format('YYYY-MM-DD HH:mm:ss') : null,
      });
      message.success('添加成员成功');
      setAddModalVisible(false);
      form.resetFields();
      fetchMembers(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.response?.data?.error || '添加成员失败');
    }
  };

  const handleDeleteMember = async (relationId) => {
    try {
      await axios.delete(`/api/agent-user-relations/${relationId}/`);
      message.success('移除成员成功');
      fetchMembers(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('移除成员失败');
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    editForm.setFieldsValue({
      start_time: member.start_time ? dayjs(member.start_time) : null,
      end_time: member.end_time ? dayjs(member.end_time) : null,
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      await axios.patch(`/api/agent-user-relations/${editingMember.id}/`, {
        start_time: values.start_time ? values.start_time.format('YYYY-MM-DD HH:mm:ss') : null,
        end_time: values.end_time ? values.end_time.format('YYYY-MM-DD HH:mm:ss') : null,
      });
      message.success('更新成功');
      setEditModalVisible(false);
      setEditingMember(null);
      fetchMembers(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('更新失败');
    }
  };

  const columns = [
    {
      title: '用户',
      dataIndex: 'user',
      key: 'user',
      render: (user) => {
        if (user && typeof user === 'object') {
          return user.username || user.email || `#${user.id}`;
        }
        const found = users.find((u) => u.id === user);
        return found?.username || '-';
      },
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      key: 'start_time',
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '结束时间',
      dataIndex: 'end_time',
      key: 'end_time',
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditMember(record)}>
            编辑
          </Button>
          <Popconfirm title="确定移除该成员？" onConfirm={() => handleDeleteMember(record.id)}>
            <Button type="link" icon={<DeleteOutlined />} danger>
              移除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{
      padding: 24,
      background: '#fffbe9',
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={onBack} style={{ padding: 0 }}>
            返回
          </Button>
          {showTitle && (
            <span style={{ fontSize: 20, fontWeight: 600 }}>
              {agentName ? `${agentName} - 成员权限` : '成员权限'}
            </span>
          )}
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
          添加成员
        </Button>
      </div>

      <div style={{
        flex: 1,
        minHeight: 0,
        background: '#fff',
        borderRadius: 10,
        boxShadow: '0 2px 8px #e5e5e5',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={members}
            scroll={tableScrollY != null ? { y: tableScrollY } : undefined}
            pagination={{
              ...pagination,
              onChange: (page, pageSize) => fetchMembers(page, pageSize),
              onShowSizeChange: (_current, size) => fetchMembers(1, size),
            }}
          />
        </div>
      </div>

      <Modal
        title="添加成员"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form form={form} onFinish={handleAddMember} layout="vertical">
          <Form.Item name="user_id" label="用户" rules={[{ required: true, message: '请选择用户' }]}>
            <Select showSearch placeholder="请选择用户" optionFilterProp="children">
              {availableUsers.map((user) => (
                <Option key={user.id} value={user.id}>
                  {user.username}{user.email ? `（${user.email}）` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="start_time" label="开始时间">
            <DatePicker showTime style={{ width: '100%' }} placeholder="可选，留空表示立即生效" />
          </Form.Item>
          <Form.Item name="end_time" label="结束时间">
            <DatePicker showTime style={{ width: '100%' }} placeholder="可选，留空表示长期有效" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="编辑成员有效期"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => editForm.submit()}
        destroyOnHidden
      >
        <Form form={editForm} onFinish={handleEditSubmit} layout="vertical">
          <Form.Item name="start_time" label="开始时间">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="end_time" label="结束时间">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentMemberManage;
